import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Container,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale,
} from "chart.js";
import { Doughnut, Radar } from "react-chartjs-2";
import {
  TrendingUp,
  AttachMoney as DollarSign,
  AccountBalance,
  CallReceived as Outflow,
  Assessment,
} from "@mui/icons-material";
import Layout from "../../components/layout/ChartsLayout";
import config from "../../config";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale
);

const theme = createTheme({
  palette: {
    primary: {
      main: "#EB5A3C",
      light: "#FF7A5C",
      dark: "#C54428",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#F8EEDB",
      light: "#FFFAF0",
      dark: "#E6D4B7",
      contrastText: "#333333",
    },
    background: {
      default: "#F8EEDB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#333333",
      secondary: "#666666",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: "0 4px 20px rgba(235, 90, 60, 0.1)",
          transition: "all 0.3s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 8px 30px rgba(235, 90, 60, 0.15)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

const BalanceSheet = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { API_BASE_URL } = config;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${API_BASE_URL}/api/v1/getFinancialReports`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success) {
          setData(result);
          setError(null);
        } else {
          throw new Error(result.message || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching financial data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatCompactCurrency = (value) => {
    if (value === null || value === undefined) return "₹0";
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(1)}Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(1)}L`;
    }
    return formatCurrency(value);
  };

  if (loading) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  textAlign: "center",
                  borderRadius: 4,
                }}
              >
                <CircularProgress size={60} sx={{ color: "#EB5A3C", mb: 2 }} />
                <Typography variant="h6" color="text.primary">
                  Loading Balance Sheet Data...
                </Typography>
              </Paper>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Alert
                severity="error"
                sx={{
                  maxWidth: 600,
                  borderRadius: 2,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Error loading balance sheet data
                </Typography>
                <Typography variant="body2">{error}</Typography>
              </Alert>
            </motion.div>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  if (!data?.success || !data?.data?.reports?.balance_sheet) {
    return (
      <Layout>
        <ThemeProvider theme={theme}>
          <Box
            sx={{
              minHeight: "100vh",
              background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Alert severity="warning">No balance sheet data available</Alert>
          </Box>
        </ThemeProvider>
      </Layout>
    );
  }

  const balanceSheetData = data.data.reports.balance_sheet?.data?.Sheet1 || [];

  // Extract key metrics
  const totalAssets =
    balanceSheetData.find((item) => item.Category === "Total Assets")?.[
      "Amount (INR)"
    ] || 0;
  const totalLiabilities =
    balanceSheetData.find((item) => item.Category === "Total Liabilities")?.[
      "Amount (INR)"
    ] || 0;
  const totalEquity =
    balanceSheetData.find((item) => item.Category === "Total Equity")?.[
      "Amount (INR)"
    ] || 0;
  const currentAssets =
    balanceSheetData.find((item) => item.Category === "Total Current Assets")?.[
      "Amount (INR)"
    ] || 0;
  const currentLiabilities =
    balanceSheetData.find(
      (item) => item.Category === "Total Current Liabilities"
    )?.["Amount (INR)"] || 0;

  // Calculate ratios
  const currentRatio =
    currentLiabilities > 0 ? currentAssets / currentLiabilities : 0;
  const debtToEquityRatio =
    totalEquity > 0 ? totalLiabilities / totalEquity : 0;
  const equityRatio = totalAssets > 0 ? totalEquity / totalAssets : 0;

  // Prepare chart data
  const assetsVsLiabilitiesData = {
    labels: ["Assets", "Liabilities", "Equity"],
    datasets: [
      {
        data: [totalAssets, totalLiabilities, totalEquity],
        backgroundColor: ["#EB5A3C", "#FF7A5C", "#F8EEDB"],
        borderColor: ["#C54428", "#E6643F", "#E6D4B7"],
        borderWidth: 2,
      },
    ],
  };

  const assetBreakdownData = {
    labels: ["Current Assets", "Non-Current Assets"],
    datasets: [
      {
        data: [currentAssets, totalAssets - currentAssets],
        backgroundColor: ["#EB5A3C", "#FF7A5C"],
        borderColor: ["#C54428", "#E6643F"],
        borderWidth: 2,
      },
    ],
  };

  const currentAssetsData = balanceSheetData.filter(
    (item) =>
      item.Category === "Current Assets" && item.Item && item["Amount (INR)"]
  );

  const currentAssetsPieData = {
    labels: currentAssetsData.map((item) => item.Item),
    datasets: [
      {
        data: currentAssetsData.map((item) => item["Amount (INR)"]),
        backgroundColor: ["#EB5A3C", "#FF7A5C", "#FFA07A", "#FFB6C1"],
        borderColor: ["#C54428", "#E6643F", "#E68A5A", "#E69BA3"],
        borderWidth: 2,
      },
    ],
  };

  const financialRatiosData = {
    labels: ["Current Ratio", "Debt-to-Equity", "Equity Ratio"],
    datasets: [
      {
        label: "Financial Ratios",
        data: [currentRatio, debtToEquityRatio, equityRatio],
        backgroundColor: "rgba(235, 90, 60, 0.2)",
        borderColor: "#EB5A3C",
        borderWidth: 2,
        pointBackgroundColor: "#EB5A3C",
        pointBorderColor: "#C54428",
        pointHoverBackgroundColor: "#C54428",
        pointHoverBorderColor: "#EB5A3C",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.label}: ${formatCompactCurrency(context.raw)}`;
          },
        },
      },
    },
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
    scales: {
      r: {
        beginAtZero: true,
        max: Math.max(currentRatio, debtToEquityRatio, equityRatio) * 1.2,
      },
    },
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Layout>
      <ThemeProvider theme={theme}>
        <Box
          sx={{
            minHeight: "100vh",
            background: "linear-gradient(135deg, #F8EEDB 0%, #FFFAF0 100%)",
            py: 4,
          }}
        >
          <Container maxWidth="xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Header */}
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: "text.primary",
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <AccountBalance sx={{ color: "primary.main" }} />
                  Balance Sheet Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Financial Position Overview
                </Typography>
              </Box>

              {/* Key Metrics Cards */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                  >
                    <Card>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            width: 56,
                            height: 56,
                            mx: "auto",
                            mb: 2,
                          }}
                        >
                          <DollarSign />
                        </Avatar>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          Total Assets
                        </Typography>
                        <Typography variant="h4" color="primary.main">
                          {formatCompactCurrency(totalAssets)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <Card>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Avatar
                          sx={{
                            bgcolor: "secondary.main",
                            color: "text.primary",
                            width: 56,
                            height: 56,
                            mx: "auto",
                            mb: 2,
                          }}
                        >
                          <Outflow />
                        </Avatar>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          Total Liabilities
                        </Typography>
                        <Typography variant="h4" color="error.main">
                          {formatCompactCurrency(totalLiabilities)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <Card>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Avatar
                          sx={{
                            bgcolor: "success.main",
                            width: 56,
                            height: 56,
                            mx: "auto",
                            mb: 2,
                          }}
                        >
                          <TrendingUp />
                        </Avatar>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          Total Equity
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {formatCompactCurrency(totalEquity)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                  >
                    <Card>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Avatar
                          sx={{
                            bgcolor: "info.main",
                            width: 56,
                            height: 56,
                            mx: "auto",
                            mb: 2,
                          }}
                        >
                          <Assessment />
                        </Avatar>
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          gutterBottom
                        >
                          Current Ratio
                        </Typography>
                        <Typography variant="h4" color="info.main">
                          {currentRatio.toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              </Grid>

              {/* Tabs */}
              <Paper sx={{ mb: 3 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  <Tab label="Overview" />
                  <Tab label="Assets Analysis" />
                  <Tab label="Financial Ratios" />
                  <Tab label="Detailed View" />
                </Tabs>
              </Paper>

              {/* Tab Panels */}
              <TabPanel value={activeTab} index={0}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: 400 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Assets vs Liabilities vs Equity
                        </Typography>
                        <Box sx={{ height: 320 }}>
                          <Doughnut
                            data={assetsVsLiabilitiesData}
                            options={chartOptions}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: 400 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Asset Composition
                        </Typography>
                        <Box sx={{ height: 320 }}>
                          <Doughnut
                            data={assetBreakdownData}
                            options={chartOptions}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: 400 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Current Assets Breakdown
                        </Typography>
                        <Box sx={{ height: 320 }}>
                          <Doughnut
                            data={currentAssetsPieData}
                            options={chartOptions}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card sx={{ height: 400 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Asset Categories
                        </Typography>
                        <List>
                          {currentAssetsData.map((item, index) => (
                            <ListItem key={index}>
                              <ListItemAvatar>
                                <Avatar
                                  sx={{
                                    bgcolor: `hsl(${index * 60}, 70%, 50%)`,
                                  }}
                                >
                                  <DollarSign />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={item.Item}
                                secondary={formatCurrency(item["Amount (INR)"])}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Card sx={{ height: 400 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Financial Ratios Analysis
                        </Typography>
                        <Box sx={{ height: 320 }}>
                          <Radar
                            data={financialRatiosData}
                            options={radarOptions}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ height: 400 }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Key Ratios
                        </Typography>
                        <Box sx={{ mt: 3 }}>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              Current Ratio
                            </Typography>
                            <Typography variant="h5" color="primary">
                              {currentRatio.toFixed(2)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Current Assets / Current Liabilities
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              Debt-to-Equity Ratio
                            </Typography>
                            <Typography variant="h5" color="error">
                              {debtToEquityRatio.toFixed(2)}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Total Liabilities / Total Equity
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              Equity Ratio
                            </Typography>
                            <Typography variant="h5" color="success.main">
                              {(equityRatio * 100).toFixed(1)}%
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Total Equity / Total Assets
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Complete Balance Sheet
                    </Typography>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Category</TableCell>
                            <TableCell>Item</TableCell>
                            <TableCell align="right">Amount (INR)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {balanceSheetData.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                {row.Category && (
                                  <Chip
                                    label={row.Category}
                                    color={
                                      row.Category.includes("Assets")
                                        ? "primary"
                                        : row.Category.includes("Liabilities")
                                        ? "error"
                                        : row.Category.includes("Equity")
                                        ? "success"
                                        : "default"
                                    }
                                    size="small"
                                  />
                                )}
                              </TableCell>
                              <TableCell>{row.Item || "-"}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: row.Category
                                      ? "bold"
                                      : "normal",
                                    color: row.Category
                                      ? "primary.main"
                                      : "text.primary",
                                  }}
                                >
                                  {formatCurrency(row["Amount (INR)"])}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </TabPanel>
            </motion.div>
          </Container>
        </Box>
      </ThemeProvider>
    </Layout>
  );
};

export default BalanceSheet;
